<template>
  <div
    @click="setSelectedWidget"
    @contextmenu="onShowContextMenu"
    :id="model.$ID"
    :class="[
      {
        'dr-data-widget-editor-mode': isInEditorMode,
        'dr-data-widget-selected': isSelected
      }
    ]"
  >
    <!-- кнопки в панели свойств -->
    <portal to="action-buttons" v-if="isSelected">
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" text color="error" @click="onDeleteComponent">
            <font-awesome-icon :icon="['fal', 'trash-alt']" size="lg" />
          </v-btn>
        </template>
        <span>{{ $t("vuetifyWidgets.common.remove") }}</span>
      </v-tooltip>
    </portal>
    <!-- режим корректировки -->
    <div v-if="isInEditorMode" class="label-data-source">
      <span class="mdi mdi-database pr-1"></span>
      <span>{{ currentDataSource.type }}: {{ currentDataSource.value }}</span>
    </div>
    <draggable
      v-if="isInEditorMode"
      class="draggable-target-data-widget"
      :group="{ name: 'container', put: ['toolbox', 'container'] }"
      :list="model.Widgets"
      :emptyInsertThreshold="20"
      :swapThreshold="0.95"
      :disabled="readonly"
      @add="onWidgetDraggableAdd"
      @update="onModelChanged"
      @change="onDraggableChanged"
      @start="onStartDraggable"
    >
      <component
        v-for="widget in model.Widgets"
        :key="widget.$ID"
        :parentsWidgets="currentListOfParentsWidgets"
        :is="widget.$Type"
        :readonly="readonly"
        :model="widget"
        @modelChanged="onModelChanged"
        @deleteComponent="onDeleteWidget"
        @replaceComponent="onReplaceWidget"
        @createSnippet="onCreateSnippetWidget"
      />
    </draggable>

    <!-- Режим отображения
    В этом режиме - обрабатываем источник данных
    -->
    <template v-else-if="innerDataSource != undefined">
      <component
        v-for="widget in model.Widgets"
        :key="widget.$ID"
        :is="widget.$Type"
        :parentDataWidget="refToThis"
        :model="widget"
        @callDataSourceAction="oncallDataSourceAction"
        :dataSource="innerDataSource"
      />
    </template>

    <!-- Контекстное меню -->
    <widgets-context-menu
      v-if="isInEditorMode"
      v-model="showContextMenu"
      :widgetModel="model"
      :x="x"
      :y="y"
      :snippetCreatable="!isInSnippetEditor"
      @createSnippet="onCreateSnippet"
      @deleteComponent="onDeleteComponent"
      @pasteCopiedWidget="onPasteCopiedWidget"
    />
  </div>
</template>

<script>
import inputMixin from '../../mixins/input-mixin'
import baseMixin from '../../mixins/base-mixin'
import microflowMixin from '../../mixins/microflow-mixin'
import contextMenuMixin from '../../mixins/contextmenu-mixin'
import widgetsWrapperMixin from '../../mixins/widgets-wrapper-mixin'
import jsonPatchMixin from '../../mixins/jsonPatchMixin'
import dataSources from '../../datasources'
import { EventBus } from '../../index'
import { getDataContract } from '../../tools/dataContractTools'
import { widgetWrappersCollection } from '../../tools/widgetWrapper/widgetWrappersCollection'

export default {
  inject: ['stateName'],
  mixins: [baseMixin, inputMixin, microflowMixin, contextMenuMixin, widgetsWrapperMixin, jsonPatchMixin],
  components: {
    'eds-create': () => import('../../widgets/dr-eds/components/eds-create/EdsCreate.vue')
  },
  provide: function () {
    return {
      // Функция, которая возращает контракт
      getDataContract: async function() {
        if (!this.$graphqlCore || !this.currentDataSourceHasContract) {
          return { contractName: '' }
        }
        return {
          contractName: await getDataContract('ObjectModel', this.dataSourceReference.Value, this.$graphqlCore.getClient)
        }
      }.bind(this)
    }
  },
  data: () => ({
    dataSourceRepository: undefined,
    validateFieldsFunctions: []
  }),
  async created() {
    // DataSource
    if (this.dataSourceReference == undefined) {
      return
    }
    // В режиме редактирования не грузим источники данных
    if (this.isInEditorMode) {
      return
    }

    // определение типа DS
    switch (this.dataSourceReference.Type) {
      // Ссылка на API
      case 'datasource':
        await this.setupDataSource()
        break
      // Локальный источник данных
      case 'context':
        await this.setupContextDataSource()
        break
      case 'microflow':
        await this.setupMicroflow()
        break
      case 'objectmodel':
        await this.setupObjectModel()
        break
      default:
        break
    }
    widgetWrappersCollection[this.model.$ID].getDataSourceRepository = () => this.dataSourceRepository
  },

  computed: {
    currentDataSourceHasContract() {
      if (!this.dataSourceReference.Value) {
        return false
      }
      if (this.dataSourceReference.Type === 'objectmodel') {
        return true
      }
      return false
    },
    refToThis() {
      return this
    },
    dataSourceReference() {
      return this.model.DataSource || {}
    },

    // получение данных из store
    innerDataSource() {
      if (!this.dataSourceRepository) {
        return undefined
      }
      return this.dataSourceRepository.getData()
    },
    currentDataSource() {
      const ds = { type: this.model.DataSource.Type, value: ''}
      switch (this.model.DataSource.Type) {
        case 'datasource': {
          ds.value = this.$store.getters.allDataSources.find(x => x.$ID === this.model.DataSource.Value)?.Name
          break
        }
        case 'microflow': {
          ds.value = this.$store.state.appModels.microflowsList.find(x => x.id === this.model.DataSource.Value)?.name
          break
        }
        case 'objectmodel': {
          ds.value = this.$store.state.appModels.objectModelsList.find(x => x.id === this.model.DataSource.Value)?.name
          break
        }
        case 'context': {
          let businessObject = this.$store.state.businessObjects.businessObjects.find(x => x.id === this.model.DataSource.Value)
          if (businessObject) {
            ds.value = `${businessObject.value} (${businessObject.id})`
          }

          break
        }
        default:
          // Локальный источник данных
          break
      }
      return ds
    }
  },

  methods: {
    // Валидация  виджетов со свойством Required, перед операцией invokeActionOnClick
    validateRequiredFields() {
      return new Promise((resolve) => {
        let result = false

        // Перебрать методы валидации всех инпут виджетов Required и вызвать их
        this.validateFieldsFunctions.forEach((element) => {
          const validateRes = element.validate(!0)
          if (validateRes) {
            result = true
          } else {
            result = false
          }
        })

        resolve(result)
      })
    },
    /** Перечитка данных из источника данных */
    async refreshDataSource() {
      // если источник данных еще не загружен - выходим
      if (!this.dataSourceRepository) {
        return
      }

      await this.dataSourceRepository.refresh()
    },

    /** Вызов действий  */
    oncallDataSourceAction({ action, params }) {
      if (!this.dataSourceRepository) {
        return
      }

      if (action === '__RereadDataSource__') {
        this.dataSourceRepository.refresh()
        return
      }

      this.dataSourceRepository.callAction(action, params)
    },

    onModelChanged() {
      this.$emit('modelChanged', this.model)
    },

    onDeleteWidget(val) {
      this.model.Widgets = this.model.Widgets.filter(comp => comp != val)
      this.$emit('modelChanged', this.model)
      this.widgetsHelper.setSelectedWidget(undefined)
    },

    onReplaceWidget({ oldModel, newModel }) {
      this.model.Widgets = this.model.Widgets.map(x => {
        return x == oldModel ? newModel : x
      })
      this.$emit('modelChanged', this.model)
      this.widgetsHelper.setSelectedWidget(newModel)
    },

    // Макет как выделеный виджет
    setSelectedWidget($event) {
      if (this.isInEditorMode) {
        $event.stopPropagation()
        this.selectComponent(this.model)
      }
    },

    onCreateSnippetWidget(payload) {
      EventBus.$emit('createSnippet', payload)
    },

    async setupDataSource() {
      const dataSource = await this.$dataSourceCore.CreateDataSource(this, this.dataSourceReference.Value)

      if (!dataSource) {
        console.warn(
          `${this.$t('vuetifyWidgets.dr-data-widget.dataSource')} '${this.dataSourceReference.Value}' ${this.$t('vuetifyWidgets.dr-data-widget.notExist')}`
        )
        return
      }

      this.dataSourceRepository = dataSource
      await this.refreshDataSource()
    },

    async setupContextDataSource() {
      const result = await dataSources.ContextDataSource()
      // Загрузка данных из DS
      this.dataSourceRepository = new result.default(
        this,
        this.model.$ID,
        this.dataSourceReference.Value
      )
      await this.refreshDataSource()
    },

    async setupMicroflow() {
      const microflowId = this.dataSourceReference.Value
      let microflowProps = this.getMicroflowParameters(this.dataSourceReference.Parameters)
      const startParams = { PageId: this.$route.name, EventName: this.dataSourceReference.EventName }
      const microflowResult = await this.$microFlowRunner.callMicroFlow(
        microflowId,
        startParams,
        microflowProps
      )

      const dsCounstructor = await dataSources.MicroflowDataSource()
      this.dataSourceRepository = new dsCounstructor.default(
        this,
        this.model.$ID,
        microflowResult.ResultValue
      )
      await this.refreshDataSource()
    },

    async setupObjectModel() {
      const objectModelParameters = this.createObjectModelParameters(this.dataSourceReference.Parameters)
      const response = await this.$objectModelRunner.executeObjectModelQuery(
        this.dataSourceReference.Value,
        objectModelParameters)

      const dsCounstructor = await dataSources.ObjectModelDataSource()
      this.dataSourceRepository = new dsCounstructor.default(
        this,
        this.model.$ID,
        response
      )
      await this.refreshDataSource()
    },

    createObjectModelParameters(parameters) {
      if (!parameters) {
        return {}
      }

      let props = {}
      for (const key in parameters) {
        props[key] = JSON.stringify(this.getExternalSourceField(parameters[key]))
      }

      return props
    }
  }
}
</script>

<style lang="scss">
// @import "../../../scss/tooltips.scss";
.dr-data-widget-editor-mode {
  border: 1px solid rgb(0, 60, 255);
  min-height: 50px;
  padding: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
}
.draggable-target-data-widget {
  height: 100%;
  min-height: 100px;
}
.dr-data-widget-selected {
  outline: 2px solid #ffa500;
  outline-offset: -2px;
}
.trash-icon {
  color: #ff5252;
}
.action-button-div {
  padding: 10px;
  margin-left: 10px;
}

.label-data-source {
  background-color: #3e77db;
  font-size: 12px;
  color: #fff;
  margin: -10px -10px 0 -10px
}
</style>

